import { LayoutSizeContext } from 'App';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WeatherWidgetDetails from 'features/Weather/components/WeatherWidgetDetails/WeatherWidgetDetails';
import WeatherWidgetSelector from 'features/Weather/components/WeatherWidgetSelector/WeatherWidgetSelector';
import MarineWidgetSelect from 'features/Weather/features/MarineForecast/components/MarineWidgetSelect/MarineWidgetSelect';
import useLocation from 'hooks/useLocation';
import { useContext, useMemo, useState } from 'react';
import { MdOutlineSurfing } from 'react-icons/md';
import { useGetDetailedMarine7Query } from 'services/weather/marine/marine.api';
import { joinClasses } from 'utils/componentStyles/style.utils';
import {
  getActiveMarineWidgetItem,
  getActiveMarineWidgetLocation,
  getMarineWidgetActiveItemIndex,
  getMarineWidgetErrorMessage,
  getMarineWidgetLocationEntries,
  getMarineWidgetLocationOptions,
  getMarineWidgetLocations,
} from '../../utils/marineForecastWidget.utils';
import './assets/surfAlertsWidget.scss';
import { details, layoutConfig, route } from './config/surfAlertsWidget.config';
import {
  getSurfAlertsWidgetLayout,
  getSurfAlertsWidgetSegmentEntries,
  getSurfAlertsWidgetTileAriaLabel,
  getSurfAlertsWidgetTileValue,
} from './utils/surfAlertsWidget.utils';
import { getSurfAlertDayGroups } from '../../features/SurfAlerts/utils/surfAlerts.utils';

const SurfAlertsWidget = ({ layout = '1x1', dimensions, containerSizePx }) => {
  const layoutSize = useContext(LayoutSizeContext);
  const { locations } = useLocation();
  const {
    data: detailedMarine = [],
    isLoading: isLoadingDetailedMarine,
    isFetching: isFetchingDetailedMarine,
    isError: isDetailedMarineError,
    error: detailedMarineError,
    refetch: refetchDetailedMarine,
    fulfilledTimeStamp,
  } = useGetDetailedMarine7Query(locations.effectiveLocation);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedDayTimestamp, setSelectedDayTimestamp] = useState(null);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(0);

  const marineLocations = useMemo(() => getMarineWidgetLocations(detailedMarine), [detailedMarine]);

  const locationOptions = useMemo(
    () => getMarineWidgetLocationOptions(marineLocations),
    [marineLocations],
  );
  const activeLocation = getActiveMarineWidgetLocation(marineLocations, selectedLocationId);

  const locationEntries = useMemo(
    () => getMarineWidgetLocationEntries(detailedMarine, activeLocation),
    [detailedMarine, activeLocation],
  );

  const dayGroups = useMemo(() => getSurfAlertDayGroups(locationEntries), [locationEntries]);

  const dayOptions = useMemo(
    () =>
      dayGroups.map(({ dayTimestamp, weekdayLabel, dateLabel }) => ({
        label: `${weekdayLabel} ${dateLabel}`,
        value: dayTimestamp,
      })),
    [dayGroups],
  );
  const activeDayGroup = getActiveMarineWidgetItem(
    dayGroups,
    selectedDayTimestamp,
    ({ dayTimestamp }) => dayTimestamp,
  );
  const segmentEntries = getSurfAlertsWidgetSegmentEntries(activeDayGroup);
  const activeSegmentIndex = getMarineWidgetActiveItemIndex(segmentEntries, selectedSegmentIndex);
  const selectedSegment = segmentEntries[activeSegmentIndex];
  const config = layoutConfig[layout] ?? layoutConfig['1x1'];
  const widgetLayout = getSurfAlertsWidgetLayout({
    containerSizePx,
    layoutSize,
    dimensions,
  });
  const isRefreshing = isFetchingDetailedMarine && !isLoadingDetailedMarine;

  if (isDetailedMarineError && !isLoadingDetailedMarine) {
    return (
      <ErrorMessage
        message={getMarineWidgetErrorMessage(detailedMarineError, 'Unable to load surf alerts.')}
        retryRoute={route}
        onRetry={() => refetchDetailedMarine()}
      />
    );
  }

  if (isLoadingDetailedMarine) {
    return <WidgetLoadingAnimation widgetName="SurfAlerts" dimensions={dimensions} />;
  }

  if (!dayGroups.length || !segmentEntries.length) {
    return (
      <section aria-label="Surf alerts widget">
        <WidgetTimeUpdate
          route={route}
          compact={config.compactTimeUpdate}
          updatedAt={fulfilledTimeStamp ?? null}
        />
        <p className="p-3 mb-0" role="status">
          No surf alert data found.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="Surf alerts widget"
      aria-busy={isRefreshing || undefined}
      className={joinClasses('surf-alerts-widget', config.wrapperClassName)}
      data-layout={layout}
    >
      <WidgetTimeUpdate
        route={route}
        compact={config.compactTimeUpdate}
        updatedAt={fulfilledTimeStamp ?? null}
      />
      {isRefreshing && (
        <span className="visually-hidden" role="status">
          Updating surf alerts.
        </span>
      )}

      <div className={config.contentClassName}>
        <div className={config.controlsClassName}>
          {locationOptions.length > 0 && (
            <MarineWidgetSelect
              label="Location"
              options={locationOptions}
              selectedValue={activeLocation?.id}
              placeholder="Select a location"
              isSearchable
              onSelect={(locationId) => {
                setSelectedLocationId(locationId ?? null);
                setSelectedDayTimestamp(null);
                setSelectedSegmentIndex(0);
              }}
            />
          )}

          <MarineWidgetSelect
            label="Forecast day"
            options={dayOptions}
            selectedValue={activeDayGroup?.dayTimestamp}
            placeholder="Select a day"
            isSearchable
            onSelect={(dayTimestamp) => {
              setSelectedDayTimestamp(dayTimestamp ?? null);
              setSelectedSegmentIndex(0);
            }}
          />
        </div>

        <div
          className="pt-2"
          style={{
            flex: `0 0 ${config.selectorHeight}`,
            height: config.selectorHeight,
            minHeight: 0,
          }}
        >
          <WeatherWidgetSelector
            items={segmentEntries}
            selectedIndex={activeSegmentIndex}
            visibleNumOfItems={config.visibleCount(widgetLayout)}
            selectorMode={config.selectorMode}
            gridColumnCount={config.gridColumnCount}
            gap={config.selectorGap}
            arrowButtonWidth={widgetLayout.arrowButtonWidth}
            arrowIconSize={widgetLayout.arrowIconSize}
            listContainerStyle={config.selectorListStyle}
            tileStyle={config.selectorTileStyle}
            ariaLabel="Surf alert forecast times of day"
            previousButtonLabel="Show previous surf alert time"
            nextButtonLabel="Show next surf alert time"
            onSelect={setSelectedSegmentIndex}
            getKey={(entry) => entry?.iso8601}
            getTileProps={(entry) => ({
              label: entry?.day_segment,
              value: getSurfAlertsWidgetTileValue(entry),
              ariaLabel: getSurfAlertsWidgetTileAriaLabel(entry),
              icon: (
                <span
                  aria-hidden="true"
                  className="surf-alerts-widget-tile-icon d-flex h-100 w-100 align-items-center justify-content-center"
                >
                  <MdOutlineSurfing />
                </span>
              ),
              iconStyle: {
                width:
                  config.selectorMode === 'row'
                    ? widgetLayout.rowTileIconSize
                    : widgetLayout.tileIconSize,
                height:
                  config.selectorMode === 'row'
                    ? widgetLayout.rowTileIconSize
                    : widgetLayout.tileIconSize,
              },
              labelStyle: widgetLayout.tileTextStyle,
              valueStyle: widgetLayout.tileTextStyle,
            })}
          />
        </div>

        <WeatherWidgetDetails
          selectedItem={selectedSegment}
          details={details}
          layoutGroups={config.detailGroups}
          containerClassName={config.detailsClassName}
          containerStyle={config.detailsStyle({ layoutSize })}
          iconSize={config.detailsIconSize}
          ariaLabel={`Surf alert details for ${activeDayGroup?.weekdayLabel ?? ''} ${
            selectedSegment?.day_segment ?? ''
          }`}
        />
      </div>
    </section>
  );
};

export default SurfAlertsWidget;
